import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Save } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function BreakingNewsManager() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ text: "", is_active: true, priority: 0 });
  const [editId, setEditId] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/breaking-news?include_inactive=true").then(r => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const handleSave = async () => {
    if (!form.text) { toast.error("Text required"); return; }
    try {
      if (editId) await api.put(`/breaking-news/${editId}`, form);
      else await api.post("/breaking-news", form);
      toast.success("Saved"); setOpen(false); setEditId(null); load();
    } catch { toast.error("Failed"); }
  };

  const handleEdit = (item) => {
    setForm({ text: item.text, is_active: item.is_active !== false, priority: item.priority || 0 });
    setEditId(item.id); setOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete?")) return;
    try { await api.delete(`/breaking-news/${id}`); toast.success("Deleted"); load(); } catch { toast.error("Failed"); }
  };

  return (
    <div data-testid="breaking-news-manager">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-serif font-bold text-foreground">Breaking News</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#1E88E5] hover:bg-[#1565C0] font-sans" onClick={() => { setForm({ text: "", is_active: true, priority: 0 }); setEditId(null); setOpen(true); }} data-testid="new-breaking-news-btn">
              <Plus className="h-4 w-4 mr-2" /> New Breaking News
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-serif">{editId ? "Edit" : "New"} Breaking News</DialogTitle></DialogHeader>
            <div className="space-y-4 pt-4">
              <div><Label className="font-sans text-sm">Text *</Label><Input value={form.text} onChange={e => setForm(p => ({ ...p, text: e.target.value }))} className="mt-1" data-testid="bn-text" /></div>
              <div><Label className="font-sans text-sm">Priority (higher = more important)</Label><Input type="number" value={form.priority} onChange={e => setForm(p => ({ ...p, priority: parseInt(e.target.value) || 0 }))} className="mt-1" data-testid="bn-priority" /></div>
              <div className="flex items-center gap-2"><Switch checked={form.is_active} onCheckedChange={v => setForm(p => ({ ...p, is_active: v }))} data-testid="bn-active" /><Label className="font-sans text-sm">Active</Label></div>
              <Button onClick={handleSave} className="w-full bg-[#1E88E5] hover:bg-[#1565C0] font-sans" data-testid="save-bn"><Save className="h-4 w-4 mr-2" />Save</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
        <Table>
          <TableHeader><TableRow>
            <TableHead className="font-sans">Text</TableHead>
            <TableHead className="font-sans">Priority</TableHead>
            <TableHead className="font-sans">Status</TableHead>
            <TableHead className="font-sans text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.map(item => (
              <TableRow key={item.id}>
                <TableCell className="font-sans text-sm max-w-md truncate">{item.text}</TableCell>
                <TableCell className="font-sans text-sm">{item.priority}</TableCell>
                <TableCell>
                  <Badge variant={item.is_active ? "default" : "secondary"} className={`text-xs font-sans ${item.is_active ? "bg-green-100 text-green-800" : ""}`}>
                    {item.is_active ? "Active" : "Inactive"}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEdit(item)}><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600" onClick={() => handleDelete(item.id)}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
